import { useState, useCallback, useRef } from 'react';
import { KanbanCard, KanbanInfiniteScrollProps } from './HBKanban.types';

/**
 * Hook to handle infinite scroll logic for Kanban
 * Manages loading state, page tracking, and calls onAppendItems to update parent state
 */
export const useKanbanInfiniteScroll = <TItemData = any>({
    columns,
    infiniteScrollProps: infiniteScroll,
    onAppendItems
}: {
    columns: { id: string }[];
    infiniteScrollProps?: KanbanInfiniteScrollProps<TItemData>;
    onAppendItems: (
        columnId: string,
        newItems: KanbanCard<TItemData>[]
    ) => void;
}) => {
    const [loadingColumns, setLoadingColumns] = useState<Set<string>>(
        new Set()
    );
    const pagesRef = useRef<Record<string, number>>(
        columns.reduce((acc, col) => {
            acc[col.id] = 1;
            return acc;
        }, {} as Record<string, number>)
    );

    const handleLoadMore = useCallback(
        async (columnId: string, page: number) => {
            if (!infiniteScroll) {
                return;
            }

            // Mark column as loading
            setLoadingColumns(prev => new Set(prev).add(columnId));

            const clearLoading = () => {
                setLoadingColumns(prev => {
                    const next = new Set(prev);
                    next.delete(columnId);
                    return next;
                });
            };

            try {
                const newItems = await infiniteScroll.loadMore(page, columnId);

                // Append new items to the parent's state
                onAppendItems(columnId, newItems);

                pagesRef.current[columnId] = page + 1;
                clearLoading();
            } catch (error) {
                // Error loading more items - silently fail
                if (process.env.NODE_ENV === 'development') {
                    // eslint-disable-next-line no-console
                    console.error('Error loading more items:', error);
                }
                clearLoading();
            }
        },
        [infiniteScroll, onAppendItems]
    );

    const isLoadingColumn = useCallback(
        (columnId: string) => {
            return loadingColumns.has(columnId);
        },
        [loadingColumns]
    );

    return {
        handleLoadMore,
        isLoadingColumn
    };
};
